<div *ngIf="loading" class="text-center py-4">
  <div class="spinner-border" role="status">
    <span class="visually-hidden">Loading...</span>
  </div>
</div>

<div *ngIf="!loading && ticket" class="row">
  <!-- Left Sidebar Tabs -->
  <div class="col-md-2">
    <div class="nav flex-column nav-pills">
      <a class="nav-link" [class.active]="activeTab === 'conversation'" (click)="setTab('conversation')" href="javascript:void(0)"> Conversation </a>
      <a class="nav-link" [class.active]="activeTab === 'decision'" (click)="setTab('decision')" href="javascript:void(0)"> Bot Decision </a>
      <a class="nav-link" [class.active]="activeTab === 'data'" (click)="setTab('data')" href="javascript:void(0)"> User Data </a>
      <a class="nav-link" [class.active]="activeTab === 'attachments'" (click)="setTab('attachments')" href="javascript:void(0)"> Attachments </a>
    </div>
  </div>

  <!-- Content -->
  <div class="col-md-10">
    <!-- Ticket Header -->
    <div class="mb-3">
      <h6 class="mb-1">{{ ticket.subject || 'No Subject' }}</h6>
      <div class="d-flex gap-2 align-items-center">
        <span class="badge" [ngClass]="getStatusClass(ticket.status)">{{ getStatusLabel(ticket.status) }}</span>
        <small class="text-muted">Created: {{ formatDate(ticket.created_at) }}</small>
        <small class="text-muted" *ngIf="ticket.requester?.email">From: {{ ticket.requester.email }}</small>
      </div>
    </div>

    <!-- Conversation Tab -->
    <div *ngIf="activeTab === 'conversation'">
      <div class="alert alert-info">
        {{ conversationLoadSummary(ticket.conversations?.length) }}
      </div>

      <!-- Original ticket description -->
      <div class="card mb-3">
        <div class="card-header d-flex justify-content-between">
          <span>
            <strong>{{ ticket.requester?.name || ticket.requester?.email || 'Customer' }}</strong>
          </span>
          <small class="text-muted">{{ formatDate(ticket.created_at) }}</small>
        </div>
        <div class="card-body">
          <div [innerHTML]="ticket.description || ticket.description_text || 'No description'"></div>
          <div *ngIf="getImageAttachments(ticket.attachments).length > 0" class="d-flex flex-wrap gap-2 mt-2 pt-2 border-top">
            <img
              *ngFor="let att of getImageAttachments(ticket.attachments)"
              [src]="att.attachment_url"
              [alt]="att.name || 'Attachment'"
              style="max-height: 120px; max-width: 200px; object-fit: cover; cursor: pointer; border-radius: 4px"
              class="border"
              (click)="openLightbox(att.attachment_url!)" />
          </div>
        </div>
      </div>

      <!-- Conversations -->
      <div *ngFor="let conv of ticket.conversations" class="card mb-3">
        <div class="card-header d-flex justify-content-between" [ngClass]="conv.private ? 'bg-light' : conv.incoming ? '' : 'bg-info bg-opacity-10'">
          <span>
            <strong>{{ conv.from_email || (conv.incoming ? 'Customer' : 'Agent') }}</strong>
            <span *ngIf="conv.private" class="badge bg-secondary ms-1">Private</span>
          </span>
          <small class="text-muted">{{ formatDate(conv.created_at) }}</small>
        </div>
        <div class="card-body">
          <div [innerHTML]="conv.body || conv.body_text || ''"></div>
          <div *ngIf="getImageAttachments(conv.attachments).length > 0" class="d-flex flex-wrap gap-2 mt-2 pt-2 border-top">
            <img
              *ngFor="let att of getImageAttachments(conv.attachments)"
              [src]="att.attachment_url"
              [alt]="att.name || 'Attachment'"
              style="max-height: 120px; max-width: 200px; object-fit: cover; cursor: pointer; border-radius: 4px"
              class="border"
              (click)="openLightbox(att.attachment_url!)" />
          </div>
        </div>
      </div>

      <div *ngIf="!ticket.conversations || ticket.conversations.length === 0" class="text-muted">No additional conversations.</div>
    </div>

    <!-- Bot Decision Tab -->
    <div *ngIf="activeTab === 'decision'">
      <div *ngIf="!decision" class="text-muted">No bot decision available for this ticket.</div>

      <div *ngIf="decision">
        <table class="table table-sm">
          <tbody>
            <tr>
              <th style="width: 200px">Action Type</th>
              <td>
                <span class="badge" [ngClass]="getActionTypeClass(decision.action.type)">{{ decision.action.type }}</span>
              </td>
            </tr>
            <tr>
              <th>Confidence</th>
              <td>{{ formatConfidence(decision.confidence) }}</td>
            </tr>
            <tr>
              <th>Autoclose</th>
              <td>
                <span class="badge" [ngClass]="decision.autoclose ? 'bg-success' : 'bg-secondary'">
                  {{ decision.autoclose ? 'Yes' : 'No' }}
                </span>
              </td>
            </tr>
            <tr>
              <th>Support Cases</th>
              <td>{{ formatSupportCaseNames(decision) }}</td>
            </tr>
            <tr *ngIf="getResolvedPhone(decision) || decision.phoneNumber">
              <th>Phone Number</th>
              <td>{{ getResolvedPhone(decision) || decision.phoneNumber }}</td>
            </tr>
            <tr *ngIf="decision.action.type === 'reply' && decision.action.summary">
              <th>Reply Summary</th>
              <td>{{ decision.action.summary }}</td>
            </tr>
            <tr *ngIf="decision.action.type === 'reply' && decision.action.message">
              <th>Proposed Reply</th>
              <td>
                <div class="border p-2 bg-light" style="white-space: pre-wrap">{{ decision.action.message }}</div>
              </td>
            </tr>
            <tr *ngIf="decision.action.type === 'reply' && decision.action.messageLanguage">
              <th>Language</th>
              <td>{{ decision.action.messageLanguage }}</td>
            </tr>
            <tr *ngIf="decision.action.type === 'reply' && decision.action.messageTranslation">
              <th>English Translation</th>
              <td>
                <div class="border p-2 bg-light" style="white-space: pre-wrap">{{ decision.action.messageTranslation }}</div>
              </td>
            </tr>
            <tr *ngIf="decision.action.type === 'close' && decision.action.summary">
              <th>Close Reason</th>
              <td>{{ decision.action.summary }}</td>
            </tr>
            <tr *ngIf="decision.action.type === 'escalate'">
              <th>Escalation Target</th>
              <td>{{ decision.action.target }}</td>
            </tr>
            <tr *ngIf="decision.action.type === 'escalate' && decision.action.reason">
              <th>Escalation Reason</th>
              <td>{{ decision.action.reason }}</td>
            </tr>
            <tr *ngIf="decision.action.type === 'no_action' && decision.action.reason">
              <th>No Action Reason</th>
              <td>{{ decision.action.reason }}</td>
            </tr>
            <tr *ngIf="decision.ticketTranslation">
              <th>Ticket Translation</th>
              <td>
                <div class="border p-2 bg-light" style="white-space: pre-wrap">{{ decision.ticketTranslation }}</div>
              </td>
            </tr>
            <tr *ngIf="decision.attachmentDescription">
              <th>Attachment Description</th>
              <td>{{ decision.attachmentDescription }}</td>
            </tr>
            <tr>
              <th>Has Video</th>
              <td>{{ decision.hasVideoAttachment ? 'Yes' : 'No' }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- User Data Tab -->
    <div *ngIf="activeTab === 'data'">
      <div *ngIf="!decision?.promptData?.dataCollection" class="text-muted">No collected user data available.</div>

      <div *ngIf="decision?.promptData?.dataCollection">
        <div class="mb-3" *ngIf="decision.promptData.dataCollection.userAccount">
          <h6>User Account</h6>
          <pre class="bg-light p-2 border" style="max-height: 400px; overflow: auto">{{ decision.promptData.dataCollection.userAccount | json }}</pre>
        </div>

        <div class="mb-3" *ngIf="decision.promptData.dataCollection.paymentRequests">
          <h6>Payment Requests</h6>
          <pre class="bg-light p-2 border" style="max-height: 400px; overflow: auto">{{
            decision.promptData.dataCollection.paymentRequests | json
          }}</pre>
        </div>

        <div class="mb-3" *ngIf="decision.promptData.dataCollection.relatedUserAccounts">
          <h6>Related User Accounts</h6>
          <pre class="bg-light p-2 border" style="max-height: 400px; overflow: auto">{{
            decision.promptData.dataCollection.relatedUserAccounts | json
          }}</pre>
        </div>

        <div class="mb-3" *ngIf="decision.promptData.dataCollection.emailLinkedUserAccount">
          <h6>Email-Linked Account</h6>
          <pre class="bg-light p-2 border" style="max-height: 400px; overflow: auto">{{
            decision.promptData.dataCollection.emailLinkedUserAccount | json
          }}</pre>
        </div>
      </div>
    </div>

    <!-- Attachments Tab -->
    <div *ngIf="activeTab === 'attachments'">
      <div *ngIf="!ticket.attachments || ticket.attachments.length === 0" class="text-muted">No attachments.</div>

      <div *ngIf="ticket.attachments && ticket.attachments.length > 0">
        <!-- Image previews -->
        <div *ngIf="getImageAttachments(ticket.attachments).length > 0" class="d-flex flex-wrap gap-2 mb-3">
          <img
            *ngFor="let att of getImageAttachments(ticket.attachments)"
            [src]="att.attachment_url"
            [alt]="att.name || 'Attachment'"
            style="max-height: 160px; max-width: 240px; object-fit: cover; cursor: pointer; border-radius: 4px"
            class="border"
            (click)="openLightbox(att.attachment_url!)" />
        </div>

        <!-- File list -->
        <ul class="list-group">
          <li *ngFor="let att of ticket.attachments" class="list-group-item d-flex justify-content-between align-items-center">
            <div>
              <strong>{{ att.name || 'Unnamed' }}</strong>
              <small class="text-muted ms-2">{{ att.content_type }}</small>
            </div>
            <a *ngIf="att.attachment_url" [href]="att.attachment_url" target="_blank" class="btn btn-sm btn-outline-primary"> Download </a>
          </li>
        </ul>
      </div>

      <div *ngIf="decision?.hasVideoAttachment" class="alert alert-info mt-2">This ticket contains a video attachment.</div>

      <div *ngIf="decision?.attachmentDescription" class="mt-2">
        <strong>AI-generated attachment description:</strong>
        <p>{{ decision.attachmentDescription }}</p>
      </div>
    </div>
  </div>
</div>

<div *ngIf="!loading && !ticket" class="text-muted text-center py-4">Ticket not found.</div>

<!-- Image Lightbox -->
<div
  *ngIf="lightboxUrl"
  (click)="closeLightbox()"
  style="
    position: fixed;
    inset: 0;
    z-index: 9999;
    background: rgba(0, 0, 0, 0.85);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
  ">
  <img [src]="lightboxUrl" style="max-width: 90vw; max-height: 90vh; object-fit: contain; border-radius: 4px" (click)="$event.stopPropagation()" />
  <button
    type="button"
    (click)="closeLightbox()"
    style="position: absolute; top: 16px; right: 24px; background: none; border: none; color: white; font-size: 2rem; cursor: pointer">
    &times;
  </button>
</div>
